import type { ParsedSheet } from '@cobliteam/hooks/src/useSpreadsheetParser';
import type { FileMetadata } from '@cobliteam/ui/components/FileUpload';
import type { WorkflowRunResult } from '@mastra/client-js';
import type { StateCreator } from 'zustand';
import type { ImportColumnDef, ImportMode, SelectedColumns } from '../types';

export interface ImportStoreState {
  columns: ImportColumnDef[];
  selectedColumns: SelectedColumns;
  isAllRequiredFieldsFilled?: boolean;
  sheetModelUrl: string;
  sheetMetaData: FileMetadata[];
  sheetTab: ParsedSheet | undefined;
  headerRow: { key: string; value: number } | undefined;
  disableNextStep: boolean;
  importMode: ImportMode | null;
  workflowResult: WorkflowRunResult | null;
  workflowRunId: string | null;
  status: string | null;
  setColumns: (columns: ImportColumnDef[]) => void;
  setSelectedColumns: (selectedColumns: SelectedColumns) => void;
  setIsAllRequiredFieldsFilled: (isFilled: boolean) => void;
  setSheetModelUrl: (url: string) => void;
  setSheetMetaData: (files: FileMetadata[]) => void;
  setSheetTab: (tab: ParsedSheet | undefined) => void;
  setHeaderRow: (row: { key: string; value: number } | undefined) => void;
  setDisabledNextStep: (disable: boolean) => void;
  setImportMode: (mode: ImportMode | null) => void;
  setWorkflowResult: (update: WorkflowRunResult | null) => void;
  setWorkflowRunId: (runId: string | null) => void;
  setStatus: (status: string) => void;
  clearSelections: () => void;
  clearAllState: () => void;
}

type StateCreatorImport = StateCreator<ImportStoreState, [['zustand/devtools', never]], [], ImportStoreState>;

const createImportStore: StateCreatorImport = (set) => ({
  columns: [],
  selectedColumns: {
    supplierName: [],
    observation: [],
  },
  isAllRequiredFieldsFilled: false,
  sheetModelUrl: '',
  sheetMetaData: [],
  sheetTab: undefined,
  headerRow: undefined,
  disableNextStep: true,
  importMode: null,
  workflowResult: null,
  workflowRunId: null,
  status: 'idle',
  setColumns: (columns) => set({ columns }),
  setSelectedColumns: (selectedColumns) => set({ selectedColumns }),
  setIsAllRequiredFieldsFilled: (isFilled) => set({ isAllRequiredFieldsFilled: isFilled }),
  setSheetModelUrl: (url) => set({ sheetModelUrl: url }),
  setSheetMetaData: (file) => set({ sheetMetaData: file }),
  setSheetTab: (tab) => set({ sheetTab: tab }),
  setHeaderRow: (row) => set({ headerRow: row }),
  setDisabledNextStep: (disable) => set({ disableNextStep: disable }),
  setImportMode: (mode) => set({ importMode: mode }),
  setWorkflowResult: (workflowResult: WorkflowRunResult | null) => set({ workflowResult }),
  setWorkflowRunId: (workflowRunId: string | null) => set({ workflowRunId }),
  setStatus: (status: string) => set({ status }),
  clearSelections: () =>
    set({
      selectedColumns: {},
      sheetTab: undefined,
      headerRow: undefined,
      sheetMetaData: [],
      disableNextStep: true,
    }),
  clearAllState: () =>
    set({
      columns: [],
      selectedColumns: {
        supplierName: [],
        observation: [],
      },
      isAllRequiredFieldsFilled: false,
      sheetModelUrl: '',
      sheetMetaData: [],
      sheetTab: undefined,
      headerRow: undefined,
      disableNextStep: true,
      importMode: null,
      workflowResult: null,
      workflowRunId: null,
      status: 'idle',
    }),
});

export default createImportStore;
